import type {
  MonthlyOccurrence,
  Occurrence,
  WeeklyOccurrence,
} from '@lotta/shared/types-lottery-schemas';
import { DateTime, Interval } from 'luxon';

/**
 * Gets the total number of draws to be made for monthly lottery
 *
 * @param monthlyOccurrence The monthly occurrence
 * @returns The total number of draws to be made
 */
export const getTotalDrawsForMonthlyLottery = ({ startDate, endDate }: MonthlyOccurrence) => {
  const startYear = Number(startDate.split('-')[0]);
  const startMonth = Number(startDate.split('-')[1]);
  const start = DateTime.fromObject({ year: startYear, month: startMonth });

  const endYear = Number(endDate.split('-')[0]);
  const endMonth = Number(endDate.split('-')[1]);
  const end = DateTime.fromObject({ year: endYear, month: endMonth }).plus({ month: 1 });

  const interval = Interval.fromDateTimes(start, end);

  return interval.length('months');
};

/**
 * Gets the total number of draws to be made for weekly lottery
 *
 * @param weeklyOccurrence The weekly occurrence
 * @returns The total number of draws to be made
 */
export const getTotalDrawsForWeeklyLottery = ({ startDate, endDate }: WeeklyOccurrence) => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  const totalNumberOfDays = Interval.fromDateTimes(start, end).length('days');

  return Math.round(totalNumberOfDays / 7);
};

/**
 * Gets the total number of draws to be made for any lottery
 *
 * @param occurrence The occurrence
 * @returns The total number of draws to be made
 */
export const getTotalDrawsForLottery = (occurrence: Occurrence) => {
  const { occurrenceType } = occurrence;
  switch (occurrenceType) {
    case 'MONTHLY':
      return getTotalDrawsForMonthlyLottery(occurrence);
    case 'WEEKLY':
      return getTotalDrawsForWeeklyLottery(occurrence);
    case 'SINGLE':
      return 1;
    default: {
      // Causes typescript-error if not all cases are handled
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustiveCheck: never = occurrenceType;
      return 0;
    }
  }
};

/**
 * Get the translation key for turning numbers of draws made and the total
 * number of draws the input into a string describing it.
 *
 * @param occurrence The occurrence of the lottery
 * @returns The translation key for formatting total and numbers of draws made
 */
export const getFormattedNbrDraws = (occurrence: Occurrence) => {
  const { occurrenceType } = occurrence;
  switch (occurrenceType) {
    case 'MONTHLY':
      return 'utilities-lottery-draw';
    case 'WEEKLY':
      return 'utilities-lottery-draw';
    case 'SINGLE':
      return 'utilities-lottery-draw';
    default: {
      // Causes typescript-error if not all cases are handled
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustiveCheck: never = occurrenceType;
      return 'utilities-lottery-draw-unknown';
    }
  }
};
